import type {
    ClassicalAPIResponseAction,
    ClassicalListShelf,
    ClassicalSectionHeading,
    ClassicalShelf,
    ClassicalShelfItem,
    ClassicalShelfItemType,
    PlayButtonAction,
} from '@jet-app/music-classical/api/types';
import type {
    ItemKind,
    Section,
} from '@jet-app/music-classical/models/section/Section';
import { transformActionUrl } from './url';
import type { ObjectGraph } from '@jet/environment/dependencies';
import { fixArtwork, type SubtitleLink } from '../../models';
import {
    createFlowActionForAction,
    createFlowActionForItem,
} from '@jet-app/music-classical/utilities/create-flow-action';
import {
    playActionForClassicalPlayAction,
    playActionForItem,
} from '@jet-app/music-classical/action-model/PlayAction';
import { makeContextualMenuFetchAction } from '@jet-app/music-classical/action-model/contextual-menu/FetchAction';
import type { Opt } from '@jet/environment';

export function transformToShelfList(
    component: ClassicalListShelf,
    title: string,
    objectGraph: ObjectGraph,
    screenType: string,
    isFromAlbum?: boolean,
): Section<unknown> {
    const itemType = component.items[0]?.type;

    // Shelves may contain lockups that have no artwork, but the other lockups do.
    // Use this flag to indicate when artwork is expected so a profile can be
    // included in the transformed item object.  The presence of the artwork
    // profile is what enables fallback artwork to be used in these cases.
    const itemsShouldHaveArtwork = component.items?.some((item) => item.image);

    return {
        id: itemType ? `${component.type}_${itemType}` : component.type,
        sectionKind: 'shelf',
        itemKind: 'shelfListItem',
        title,
        items:
            itemType === 'subheading'
                ? transformTracksForShelfList(component.items, objectGraph)
                : component.items?.map((item) => {
                      const shelfItem = getItemForType(
                          item,
                          objectGraph,
                          null,
                          screenType,
                          itemsShouldHaveArtwork,
                      );
                      if (isFromAlbum) {
                          shelfItem.isFromAlbum = true;
                      }
                      return shelfItem;
                  }),
    };
}

// TODO map shelfItemType -> component, components TBD
const SHELF_ITEM_TYPE_TO_LOCKUP_TYPE: {
    [key in ClassicalShelfItemType]: ItemKind;
} = {
    featured: 'flowcaseLockup',
    standard: 'squareLockup',
};

const COMPONENT_TYPE_TO_LOCKUP_TYPE = {
    artist: 'ellipseLockup',
    playableListItem: 'editorsChoiceLockup',
    playlist: 'squareLockup',
    album: 'squareLockup',
    instrument: 'brickLockup',
    'album-with-actions': 'squareLockup',
};

export function transformToShelf(
    component: ClassicalShelf,
    heading: ClassicalSectionHeading,
    kind: string,
    objectGraph: ObjectGraph,
): Section<unknown> | null {
    if (component.items.length === 0) {
        return null;
    }
    // Item Kind can be based on the Component itemType or based on the first item's type if itemType for whole section not present
    const itemKind =
        SHELF_ITEM_TYPE_TO_LOCKUP_TYPE[component.itemType] ||
        COMPONENT_TYPE_TO_LOCKUP_TYPE[component.items[0]?.type];

    const includeOrdinals =
        heading?.button?.action?.location?.includes('/charts');
    return {
        id: heading?.title
            ? `${component.type}_${heading?.title
                  .toLowerCase()
                  .replaceAll(' ', '_')}`
            : component.type,
        sectionKind: 'shelf',
        itemKind,
        title: heading?.title,
        items: component.items?.map((item, index) => {
            return getItemForType(
                item,
                objectGraph,
                includeOrdinals ? index + 1 : null,
            );
        }),
        segue: createFlowActionForAction(heading?.button?.action, objectGraph, {
            section: heading?.button?.action?.location,
            query: heading?.button?.action?.url,
        }),
    };
}

function getItemForType(
    item: ClassicalShelfItem,
    objectGraph: ObjectGraph,
    ordinal?: number,
    screenType?: string,
    itemShouldHaveArtwork?: boolean,
) {
    const canonicalUrl = transformActionUrl(item.action?.url);
    const additionalParams = !!canonicalUrl ? { url: canonicalUrl } : undefined;
    let attributes: any = {
        action: item.action,
        playAction: playActionForItem(item),
        segue:
            createFlowActionForItem(item, objectGraph, additionalParams) ??
            null,
        subtitle: item.subtitle,
        title: item.title,
        type: item.type,
    };

    // Check for subtitleActions and transform to subtitleLinks
    // except for Recordings, as the title name in this lockup format
    // will be the artist names, so we need to just show subtitle.
    if (item?.subtitleActions && item?.type !== 'recording') {
        attributes.subtitleLinks = Array.isArray(item.subtitleActions)
            ? item.subtitleActions
                  .map((action) => getSubtitleLink(action, objectGraph))
                  .filter(Boolean)
            : [];
    }

    if (item.action?.kind === 'editorialStation') {
        attributes.playAction = playActionForClassicalPlayAction(
            item.action as PlayButtonAction,
        );
    }

    // Make contextualmenu fetch
    if (item?.contextMenuAction?.url) {
        attributes.contextMenuAction = makeContextualMenuFetchAction(
            item.contextMenuAction.url,
        );
    }

    // Generic attributes
    // =============================

    if (!!canonicalUrl) {
        // Ensure we have a url property that will generate a link preview
        attributes.url = canonicalUrl;
    }

    if (ordinal) {
        attributes.ordinal = ordinal;
    }

    if (item.image) {
        attributes.artwork = fixArtwork(item.image);
    }

    // Attributes by type
    // =============================

    if (item.type === 'featured') {
        attributes.addition = item.addition;
        attributes.coverArtwork = fixArtwork(item.coverImage);
    }

    if (item.type === 'playableListItem') {
        attributes.headline = item.subtitle;
        attributes.subtitle = item.description;
    }

    if (item.type === 'work') {
        attributes = {
            ...attributes,
            headline: item.composerName?.toUpperCase(),
            subtitle: item.opusAndAltTitle,
            actionText: item.recordingsNumber,
        };
    }

    if (
        [
            'album',
            'album-with-actions',
            'playlist',
            'track',
            'recording',
        ].includes(item.type)
    ) {
        attributes = {
            ...attributes,
            artworkProfile: 'episode-lockup',
        };
    }

    // For Work Shelf, only render ellipse lockup if artwork present OR on search page
    // OR if on a shelf where other items have artwork.  The presence of the profile
    // is what allows the fallback artwork in these cases to display.
    if (
        item.type === 'work' &&
        (attributes.artwork ||
            screenType === 'searchResult' ||
            itemShouldHaveArtwork)
    ) {
        attributes = {
            ...attributes,
            artworkProfile: 'ellipse-lockup',
        };
    }

    if (item.type === 'album-with-actions') {
        attributes.type = 'album';
    }

    return attributes;
}

export function getSubtitleLink(
    action,
    objectGraph: ObjectGraph,
): Opt<SubtitleLink> {
    if (!action) {
        return null;
    }

    if (typeof action === 'string') {
        return {
            title: action,
        };
    }

    return {
        segue: createFlowActionForAction(action, objectGraph),
        title: action.title || action.subtitle, // or any other default title you want to set
        href: transformActionUrl(action.url), // https://classical.music.apple.com/us/artist/306830056
    };
}

/**
 * Method to transform Tracks & SubHeading shelf list items to unified Track Shelf List Items with header & action
 * @param items
 * @param objectGraph
 * @returns Array of Track Shelf List Items
 */
function transformTracksForShelfList(
    items: ClassicalShelfItem[],
    objectGraph: ObjectGraph,
): any[] {
    let currentHeader = null;
    const tracksWithHeader = items.reduce((acc, item, idx) => {
        const isWorkHeader = item?.type === 'subheading' || false;

        if (isWorkHeader) {
            currentHeader = item;
        } else {
            acc.push({
                ...getItemForType(item, objectGraph),
                playAction: playActionForClassicalPlayAction(
                    item?.action as unknown as ClassicalAPIResponseAction,
                ),
                header: {
                    title: currentHeader?.title,
                    segue: createFlowActionForAction(
                        currentHeader?.action,
                        objectGraph,
                        {
                            section: currentHeader?.action?.location,
                            query: currentHeader?.action?.url,
                        },
                    ),
                },
            });
        }

        return acc;
    }, []);
    return tracksWithHeader;
}
